import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import CustomerHeader from '../../components/ui/CustomerHeader';
import CustomerBottomTabs from '../../components/ui/CustomerBottomTabs';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import ThingInfo from './components/ThingInfo';
import ThingImageDisplay from './components/ThingImageDisplay';
import PurchaseControls from './components/PurchaseControls';
import RelatedThings from './components/RelatedThings';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useThingDetail } from '../../hooks/useThingDetail';

const ThingDetailScreen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [cartItemCount, setCartItemCount] = useState(3);
  const [favoriteCount, setFavoriteCount] = useState(12);
  const [relatedThings, setRelatedThings] = useState([]);

  const thingId = searchParams?.get('id') || '1';

  const {
    thing,
    loading,
    error,
    refreshThing,
    toggleFavorite: toggleThingFavorite,
    fetchRelatedThings,
  } = useThingDetail(thingId);

  const breadcrumbItems = [
    { label: 'Catálogo', path: '/book-catalog-browse-screen' },
    { label: 'Accesorios', path: '/book-catalog-browse-screen?type=thing' },
    { label: thing?.title || 'Accesorio', path: '' },
  ];

  useEffect(() => {
    const loadRelatedThings = async () => {
      if (thing) {
        const related = await fetchRelatedThings();
        setRelatedThings(related);
      }
    };
    loadRelatedThings();
  }, [thing, fetchRelatedThings]);

  const handleToggleFavorite = async (thingId, isFavorite) => {
    try {
      const success = await toggleThingFavorite(isFavorite);
      if (success) {
        setFavoriteCount((prev) => (isFavorite ? prev + 1 : prev - 1));
      }
      return success;
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return false;
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <CustomerHeader cartItemCount={cartItemCount} />
        <main className="pt-16 pb-20 lg:pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="animate-pulse">
              <div className="lg:grid lg:grid-cols-12 lg:gap-8">
                <div className="lg:col-span-5">
                  <div className="aspect-[3/4] bg-muted rounded-lg mb-4"></div>
                </div>
                <div className="lg:col-span-7 space-y-4">
                  <div className="h-8 bg-muted rounded w-3/4"></div>
                  <div className="h-6 bg-muted rounded w-1/2"></div>
                  <div className="h-10 bg-muted rounded w-1/3"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-muted rounded"></div>
                    <div className="h-4 bg-muted rounded w-5/6"></div>
                    <div className="h-4 bg-muted rounded w-4/6"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <CustomerBottomTabs cartItemCount={cartItemCount} favoriteCount={favoriteCount} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <CustomerHeader cartItemCount={cartItemCount} />
        <main className="pt-16 pb-20 lg:pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center py-12">
              <Icon name="AlertTriangle" size={64} className="mx-auto text-muted-foreground mb-4" />
              <h1 className="text-2xl font-heading font-bold text-foreground mb-2">
                Error al cargar el accesorio
              </h1>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Button onClick={handleGoBack}>
                <Icon name="ArrowLeft" size={20} className="mr-2" />
                Volver
              </Button>
            </div>
          </div>
        </main>
        <CustomerBottomTabs cartItemCount={cartItemCount} favoriteCount={favoriteCount} />
      </div>
    );
  }

  if (!thing) {
    return (
      <div className="min-h-screen bg-background">
        <CustomerHeader cartItemCount={cartItemCount} />
        <main className="pt-16 pb-20 lg:pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center py-12">
              <Icon name="Package" size={64} className="mx-auto text-muted-foreground mb-4" />
              <h1 className="text-2xl font-heading font-bold text-foreground mb-2">
                Accesorio no encontrado
              </h1>
              <p className="text-muted-foreground mb-6">
                El accesorio que buscas no existe o ha sido eliminado.
              </p>
              <Button onClick={handleGoBack}>
                <Icon name="ArrowLeft" size={20} className="mr-2" />
                Volver
              </Button>
            </div>
          </div>
        </main>
        <CustomerBottomTabs cartItemCount={cartItemCount} favoriteCount={favoriteCount} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <CustomerHeader cartItemCount={cartItemCount} />
      <main className="pt-16 pb-20 lg:pb-8">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="lg:hidden py-4">
            <Button
              variant="ghost"
              onClick={handleGoBack}
              className="p-0 h-auto text-muted-foreground hover:text-foreground"
            >
              <Icon name="ArrowLeft" size={20} className="mr-2" />
              Volver
            </Button>
          </div>

          <div className="hidden lg:block">
            <BreadcrumbTrail items={breadcrumbItems} />
          </div>

          <div className="lg:grid lg:grid-cols-12 lg:gap-8">
            <div className="lg:col-span-5 space-y-4 sm:space-y-6">
              <ThingImageDisplay thing={thing} />
            </div>

            <div className="lg:col-span-7 mt-4 sm:mt-6 lg:mt-0">
              <div className="space-y-4 sm:space-y-6">
                <ThingInfo thing={thing} />
                <div className="lg:max-w-md">
                  <PurchaseControls thing={thing} />
                </div>
                {relatedThings?.length > 0 && (
                  <div className="mt-12">
                    <RelatedThings things={relatedThings} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <CustomerBottomTabs cartItemCount={cartItemCount} favoriteCount={favoriteCount} />
    </div>
  );
};

export default ThingDetailScreen;

